import React, { useState, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol, inputStyle, captionStyle } from "styles/global-style";
import Button from "components/Button";

// Escape a value for CSV (quote when it contains separators/quotes/newlines)
const csvField = (value) => {
  const str = String(value ?? "");
  if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
  return str;
};

// Parse "YYYY-MM-DD" as UTC midnight so weekday math is timezone-safe
const toUtcDate = (stamp) => new Date(stamp + "T00:00:00Z");

// Next weekday after a UTC date (weekends are never stored as unavailability)
const nextWeekday = (date) => {
  const next = new Date(date);
  do {
    next.setUTCDate(next.getUTCDate() + 1);
  } while (next.getUTCDay() === 0 || next.getUTCDay() === 6);
  return next;
};

export default function Export() {
  const { supabase, user } = useContext(AuthContext);

  const currentYear = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${currentYear}-01-01`);
  const [toDate, setToDate] = useState(`${currentYear}-12-31`);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleExport = useCallback(async () => {
    setError(null);
    setResult(null);

    if (!fromDate || !toDate) {
      setError("Please select both a from and a to date.");
      return;
    }
    if (fromDate > toDate) {
      setError("The from date must be before the to date.");
      return;
    }

    setExporting(true);
    try {
      // Same shape as the Timeline fetch: everyone in the org with their
      // typed days (sick/vacation/etc.) joined to the day type details.
      const res = await supabase
        .from("memberships")
        .select("*, people(*, days(*, day_type(*)))")
        .eq("org_id", user.org.id)
        .neq("status", "deleted")
        .order("date", { foreignTable: "people.days", ascending: true });

      if (res.error) throw res.error;

      const rows = [];
      let peopleWithEntries = 0;

      for (const member of res.data || []) {
        const person = member.people;
        if (!person) continue;

        // Only typed days (unavailability) within the selected range.
        // Rows without a day_type are join/leave markers or plain working days.
        const entries = (person.days || [])
          .filter((day) => day.day_type && day.date)
          .map((day) => ({ stamp: day.date.slice(0, 10), type: day.day_type }))
          .filter((day) => day.stamp >= fromDate && day.stamp <= toDate)
          .sort((a, b) => (a.stamp < b.stamp ? -1 : 1));

        if (!entries.length) continue;
        peopleWithEntries++;

        // Collapse consecutive weekdays of the same type into one period
        let period = null;
        const periods = [];
        for (const entry of entries) {
          const date = toUtcDate(entry.stamp);
          if (period && period.type.id === entry.type.id && nextWeekday(period.endDate).getTime() === date.getTime()) {
            period.end = entry.stamp;
            period.endDate = date;
            period.workdays++;
          } else {
            period = { type: entry.type, start: entry.stamp, end: entry.stamp, endDate: date, workdays: 1 };
            periods.push(period);
          }
        }

        for (const p of periods) {
          rows.push([person.firstname || "", person.lastname || "", person.email || "", p.type.name, p.start, p.end, p.workdays]);
        }
      }

      if (!rows.length) {
        setError("No unavailability found in the selected date range.");
        return;
      }

      const header = ["First name", "Last name", "Email", "Type", "From", "To", "Workdays"];
      const csv = [header, ...rows].map((row) => row.map(csvField).join(",")).join("\n");

      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `unavailability_${fromDate}_${toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setResult(`Exported ${rows.length} period${rows.length === 1 ? "" : "s"} for ${peopleWithEntries} ${peopleWithEntries === 1 ? "person" : "people"}.`);
    } catch (err) {
      console.error("Error exporting unavailability:", err);
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  }, [supabase, user, fromDate, toDate]);

  const dateInputStyle = { ...inputStyle, textAlign: "left", border: "1px solid #bbb", minWidth: 0, width: 160 };

  return (
    <FlexLeftCol>
      <h2>Export unavailability</h2>

      <p style={{ color: "#666", margin: 0 }}>
        Download a CSV of everyone's unavailability (vacation, sick days, parental leave, etc.) within a date range. Consecutive days of the same type are grouped into periods.
      </p>

      <div style={{ display: "flex", columnGap: 16, alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ ...captionStyle, color: "#999" }}>From</div>
          <input type="date" className="input" style={dateInputStyle} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ ...captionStyle, color: "#999" }}>To</div>
          <input type="date" className="input" style={dateInputStyle} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      <div>
        <Button text={exporting ? "Exporting..." : "Download CSV"} callback={handleExport} color="purple" />
      </div>

      {error && (
        <div style={{ background: "#f8d7da", padding: 12, borderRadius: 8, border: "1px solid #dc3545", color: "#721c24" }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{ background: "#d4edda", padding: 12, borderRadius: 8, border: "1px solid #28a745", color: "#155724" }}>
          {result}
        </div>
      )}
    </FlexLeftCol>
  );
}
